import { type Actr } from '@opcentral/typr-lib';

/**
 * LogEntry.ts - LogEntry Model
 * 
 * CHANGE HISTORY:
 * - 251226_0840 [active] danielbeer: Remove @ts-nocheck and fix type errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
// import { generateId } from '@opcentral/core-lib';
const generateId = () => Math.random().toString(36).substring(2, 15);

/**
 * LogLevel constant
 */
export const LogLevel = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  FATAL: 5
};

/**
 * LogLevelNames constant
 */
export const LogLevelNames = {
  0: 'TRACE',
  1: 'DEBUG',
  2: 'INFO',
  3: 'WARN',
  4: 'ERROR',
  5: 'FATAL'
};


export type LogLevelValue = 0 | 1 | 2 | 3 | 4 | 5;

interface LogEntryData {
  id?: string;
  timestamp?: number;
  level?: LogLevelValue;
  message?: string;
  category?: string;
  userId?: string;
  sessionId?: string;
  context?: Record<string, any>;
  tags?: string[];
  metadata?: Record<string, any>;
  error?: Error;
  stack?: string;
  service?: string;
  environment?: string;
}

/**
 * LogEntry class
 */
export class LogEntry {
  id: string;
  timestamp: string | number;
  level: LogLevelValue;
  levelName: string;
  message: string;
  category: string;
  userId: string | null;
  sessionId: string | null;
  context: Record<string, any>;
  error: Error | null;
  stack: string | null;
  tags: string[];
  metadata: Record<string, any>;
  service?: string;
  environment?: string;

  constructor(data: LogEntryData = {}) {
    this.id = data.id || generateId();
    this.timestamp = data.timestamp || Date.now();
    this.level = (data.level !== undefined ? data.level : LogLevel.INFO) as LogLevelValue;
    this.levelName = LogLevelNames[this.level];
    this.message = data.message || '';
    this.category = data.category || 'general';
    this.userId = data.userId || null;
    this.sessionId = data.sessionId || null;
    this.context = data.context || {};
    this.error = data.error || null;
    this.stack = data.stack || null;
    this.tags = data.tags || [];
    this.metadata = data.metadata || {};
    this.service = data.service;
    this.environment = data.environment;
  }
  
  /**
   * Check if log level is enabled
   */
  isLevelEnabled(minLevel: LogLevelValue): boolean {
    return this.level >= minLevel;
  }
  
  /**
   * Format for console output
   */
  toConsoleString(): string {
    const timestamp = typeof this.timestamp === 'number' 
      ? new Date(this.timestamp).toISOString() 
      : this.timestamp;
    const level = this.levelName.padEnd(5);
    const category = this.category.padEnd(15);
    return `[${timestamp}] ${level} [${category}] ${this.message}`;
  }
  
  /**
   * Format for syslog (RFC 5424)
   */
  toSyslogString(): string {
    const priority = this.getSyslogPriority();
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const appName = 'opcentral';
    const procId = this.sessionId || '-';
    const msgId = this.category;
    const timestamp = typeof this.timestamp === 'number' 
      ? new Date(this.timestamp).toISOString() 
      : this.timestamp;
    
    return `<${priority}>1 ${timestamp} ${hostname} ${appName} ${procId} ${msgId} - ${this.message}`;
  }
  
  /**
   * Get syslog priority
   */
  getSyslogPriority(): number {
    const facility = 1;
    const severityMap: Record<number, number> = {
      [LogLevel.TRACE]: 7,
      [LogLevel.DEBUG]: 7,
      [LogLevel.INFO]: 6,
      [LogLevel.WARN]: 4,
      [LogLevel.ERROR]: 3,
      [LogLevel.FATAL]: 0
    };
    const severity = severityMap[this.level] || 6;
    return (facility * 8) + severity;
  }
  
  /**
   * Format for JSON logging
   */
  toJSON(): Record<string, any> {
    const json: Record<string, any> = {
      id: this.id,
      timestamp: this.timestamp,
      level: this.level,
      message: this.message,
    };

    if (this.category) json.category = this.category;
    if (this.userId) json.userId = this.userId;
    if (this.sessionId) json.sessionId = this.sessionId;
    if (this.context && Object.keys(this.context).length > 0) json.context = this.context;
    if (this.tags && this.tags.length > 0) json.tags = this.tags;
    if (this.metadata && Object.keys(this.metadata).length > 0) json.metadata = this.metadata;
    if (this.error) json.error = {
      message: this.error.message,
      stack: this.error.stack
    };
    if (this.stack) json.stack = this.stack;

    return json;
  }
  
  /**
   * Format for structured logging (ELK, Splunk, etc.)
   */
  toStructuredLog(): Record<string, any> {
    const timestamp = typeof this.timestamp === 'number' 
      ? new Date(this.timestamp).toISOString() 
      : this.timestamp;
      
    const structured: Record<string, any> = {
      '@timestamp': timestamp,
      level: this.levelName,
      message: this.message,
    };

    if (this.category) structured.logger = this.category;
    if (this.userId) structured.user_id = this.userId;
    if (this.sessionId) structured.session_id = this.sessionId;
    if (this.tags && this.tags.length > 0) structured.tags = this.tags;
    if (this.context && Object.keys(this.context).length > 0) {
      Object.assign(structured, this.context);
    }
    if (this.metadata && Object.keys(this.metadata).length > 0) {
      Object.assign(structured, this.metadata);
    }

    if (this.error) {
      structured.error = {
        type: this.error.name,
        message: this.error.message,
        stack_trace: this.error.stack
      };
    }

    return structured;
  }

  static fromJSON(json: LogEntryData): LogEntry {
    return new LogEntry(json);
  }
}

export default LogEntry;
